import SortableMediaItem from "@/components/community/SortableMediaItem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { createComment, IComment, uploadImageToStorage } from "@/lib/community";
import { getAvatarUrl } from "@/lib/userApi";
import { cn } from "@/lib/utils";
import { getEmbeddedVideoUrl } from "@/utils/getEmbed";
import { getInitials } from "@/utils/getInitials";
import { closestCenter, DndContext } from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import * as Avatar from "@radix-ui/react-avatar";
import { useUser } from "@supabase/auth-helpers-react";
import { ImagePlus, Video, X } from "lucide-react";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import InfiniteTextarea from "./InfiniteTextarea";
import { useTranslation } from "react-i18next";
import { MixpanelActions } from "@/integrations/mixpanel/mixpanel";
import { useQueryClient } from "@tanstack/react-query";

interface IProps {
  postId: string | number;
  parentId?: string | number;
  onCommentAdd: (comment: IComment) => void;
  onCancel?: () => void;
}
const CommentForm = forwardRef<HTMLDivElement, IProps>(
  ({ postId, parentId, onCommentAdd, onCancel }, ref) => {
    const { t } = useTranslation();
    const user = useUser();
    const { subscription } = useSubscription();
    const [formClicked, setFormClicked] = useState(false);
    const [content, setContent] = useState("");
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [videoUrlInput, setVideoUrlInput] = useState("");
    const [media, setMedia] = useState<
      { id: string; type: "image" | "video"; url: string }[]
    >([]);
    const queryClient = useQueryClient();
    const formRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const handleSubmitComment = async () => {
      if (!content) return;

      const comment = await createComment({
        content: content,
        media: media,
        author: user.id,
        post_id: postId,
        parent_id: parentId,
      });
      if (comment) MixpanelActions.createComment(comment);
      if (comment)
        onCommentAdd({
          ...comment,
          authorData: {
            username: subscription.username,
            avatar: subscription.avatar,
          },
        });
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      setContent("");
      setMedia([]);
      setFormClicked(false);
    };

    const handleAddVideo = () => {
      const url = videoUrlInput.trim();

      if (!url) return;

      const isDirectVideo = url.match(/\.(mp4|webm|ogg)(\?.*)?$/i);

      const embedUrl = getEmbeddedVideoUrl(url);

      if (isDirectVideo || embedUrl) {
        setMedia((prev) => [
          ...prev,
          { id: uuidv4(), type: "video", url: isDirectVideo ? url : embedUrl },
        ]);
        setVideoModalOpen(false);
        setVideoUrlInput("");
      }
    };

    const handleImageUpload = async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const imageUrl = await uploadImageToStorage(file);
      if (imageUrl) {
        setMedia((prev) => [
          ...prev,
          { id: uuidv4(), type: "image", url: imageUrl },
        ]);
      }
    };

    const triggerImageUpload = () => fileInputRef.current?.click();

    const handleDragEnd = (event: any) => {
      const { active, over } = event;
      setIsDragging(false);

      if (active.id !== over?.id) {
        setMedia((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    };

    const removeMedia = (id: string) => {
      setMedia((prev) => prev.filter((item) => item.id !== id));
    };

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          formRef.current &&
          !formRef.current.contains(event.target as Node) &&
          videoModalOpen
        ) {
          setFormClicked(false);
        }
      }
      if (formClicked) {
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [formClicked]);

    const handleDragStart = () => {
      setIsDragging(true);
    };

    const handleCancel = () => {
      setContent("");
      setMedia([]);
      setFormClicked(false);
      if (onCancel) onCancel();
    };
    return (
      <>
        <div className="mt-6 flex items-start gap-2" ref={ref}>
          <div className="flex items-center gap-4">
            <Avatar.Root className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
              <Avatar.Image
                className="w-full h-full object-cover"
                src={getAvatarUrl(subscription.avatar)}
                alt="User Avatar"
              />
              <Avatar.Fallback className="text-sm font-medium text-gray-600">
                {getInitials(subscription.username)}
              </Avatar.Fallback>
            </Avatar.Root>
          </div>
          <div ref={formRef} className=" bg-background w-full">
            <div
              className={cn(
                "w-full px-3 bg-background border-[1px] border-gray-600 rounded-xl dark:text-white h-auto"
              )}
            >
              <InfiniteTextarea
                value={content}
                onChange={setContent}
                placeholder={t("community.commentForm.yourComment")}
                className="max-w-full "
              />
              <div className="w-full basis-full">
                {media.length > 0 && (
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={media}
                      strategy={horizontalListSortingStrategy}
                    >
                      <div className="flex space-x-2 overflow-x-auto mt-4 overflow-y-hidden">
                        {media.map((item) => (
                          <div
                            key={item.id}
                            className="relative group w-[100px] h-[100px]"
                          >
                            <SortableMediaItem id={item.id}>
                              <div className="w-[100px] h-[100px]">
                                {item.type === "image" ? (
                                  <img
                                    src={item.url}
                                    alt="Uploaded"
                                    className="rounded-lg w-full h-full object-cover"
                                  />
                                ) : item.url.includes("embed") ? (
                                  <iframe
                                    src={item.url}
                                    className="rounded-lg w-full h-full object-cover"
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                  />
                                ) : (
                                  <video
                                    src={item.url}
                                    controls
                                    className="rounded-lg w-full h-full object-cover"
                                  />
                                )}
                              </div>
                            </SortableMediaItem>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "absolute top-2 right-2 w-6 h-6 bg-white/80 z-10",
                                isDragging && "hidden"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeMedia(item.id);
                              }}
                            >
                              <X className="h-4 w-4 text-gray-700" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageUpload}
                className="hidden"
              />
              <div
                className={cn(
                  "flex justify-end items-center gap-2 pb-2 text-gray-500 text-sm",
                  {
                    // "absolute top-0 right-2": !media.length || !content,
                  }
                )}
              >
                <Button
                  variant="add_file"
                  size="icon"
                  onClick={() => setVideoModalOpen(true)}
                >
                  <Video />
                </Button>
                <Button
                  variant="add_file"
                  size="icon"
                  onClick={triggerImageUpload}
                >
                  <ImagePlus />
                </Button>
              </div>
            </div>

            <div className="w-full flex justify-end mt-4">
              <div className="flex gap-2">
                {content || media.length ? (
                  <>
                    <Button variant="ghost" onClick={handleCancel}>
                      {t("community.commentForm.cancel")}
                    </Button>
                    <Button onClick={handleSubmitComment} disabled={!content}>
                      {t("community.commentForm.reply")}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {videoModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">
                {t("community.commentForm.addVideoLink")}
              </h2>
              <Input
                placeholder={t("community.commentForm.videoPlaceholder")}
                value={videoUrlInput}
                onChange={(e) => setVideoUrlInput(e.target.value)}
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="ghost"
                  onClick={() => setVideoModalOpen(false)}
                >
                  {t("community.commentForm.cancel")}
                </Button>
                <Button
                  onClick={handleAddVideo}
                  disabled={!videoUrlInput.trim()}
                >
                  {t("community.commentForm.insert")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default CommentForm;
